import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/auth';
import { useNotification } from '../sharedComponents/Notifications';

interface RouteGuardProps {
  requiresMenuAccess?: boolean;
}

const RouteGuard: React.FC<RouteGuardProps> = ({ requiresMenuAccess = true }) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const { showNotification } = useNotification();

  // 1️⃣ Not authenticated → send to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // 2️⃣ Authors never need a menu check
  if (user.role === 'author' || !requiresMenuAccess) {
    return <Outlet />;
  }

  // 3️⃣ Read menus from localStorage and compare against current path
  const stored = localStorage.getItem('menus');
  const allowedMenus: { route: string }[] = stored ? JSON.parse(stored) : [];

  let hasAccess = allowedMenus.some(menu => {
    const route = `/${menu.route}`;
    return location.pathname === route || location.pathname.startsWith(route + '/');
  });

  // Implicit access for related sub-pages
  if (!hasAccess && location.pathname === '/admin/final-assessment') {
    hasAccess = allowedMenus.some(menu => menu.route === 'admin/collective-assessment');
  }

  if (!hasAccess) {
    showNotification('error', {
      title: 'Access Denied',
      message: 'You do not have access to this route',
      timer: 4000,
    });
    return <Navigate to="/admin" replace />;
  }

  return <Outlet />;
};

export default RouteGuard;